import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMessage } from "../context/MessageContext";
import { useApp } from "../context/AppContext";
import { loginUser, lostPassword } from "../apis/apiList/authApi";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import "../css/loginCss/Login.css";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showMessage } = useMessage();
  const { login } = useApp();
  const [isLogin, setIsLogin] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [isLostPass, setIsLostPass] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    let newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 3) {
      newErrors.password = "Password must be at least 3 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEmail = () => {
    let newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLogin(true);

    if (validateForm()) {
      const userData = { email: formData.email, password: formData.password };
      try {
        const response = await loginUser(userData);
        if (response.success) {
          login(response.user);
          if (response.user.role === 'admin') {
            navigate("/task/list");
            setIsLogin(false);
          } else {
            if (response.user.orgId) {
              navigate(`/subs/org/${response.user.orgId}/projects`);
            } else {
              navigate("/select-organization");
            }
            setIsLogin(false);
          }
          showMessage("User logged in successfully", "success");
        }
      } catch (err) {
        showMessage(err?.response?.data?.message, "error");
        setIsLogin(false);
      }
    } else {
      setIsLogin(false);
    }
  };

  const handleLostPassSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    if (validateEmail()) {
      const userData = { email: formData.email };
      try {
        const response = await lostPassword(userData);
        if (response.success) {
          showMessage(response.message, 'success');
          setForgotLoading(false);
        }
      } catch (err) {
        showMessage(err?.response?.data?.message, "error");
        setForgotLoading(false);
      }
    } else {
      setForgotLoading(false);
    }
  };

  const handleLinkedInLogin = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/v1/linkedin/auth`;
  };

  const handleLostPass = () => {
    setIsLostPass(!isLostPass);
  };

  // Show authentication error from LinkedIn redirect (?error=authentication-failed)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      const normalized = errorParam.toLowerCase();
      if (
        normalized === "authentication-failed"
      ) {
        showMessage("Authentication failed. Please try again.", "error");
        // Remove the error query so the message doesn't reappear after dismissing
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete("error");
        navigate({
          pathname: location.pathname,
          search: nextParams.toString() ? `?${nextParams.toString()}` : ""
        }, { replace: true });
      }
    }
  }, [location.search, showMessage]);

  return (
    <div className="login-page">
      <Header />
      
      <section className="login-section">
        <div className="container">
          <div className="login-wrapper">
            <div className="login-content">
              {isLostPass ?<div className="login-header">
                <h1>Forgot Your Password?</h1>
                <p style={{textAlign: 'center'}}>Enter your email to reset your password</p>
              </div>
              :
              <div className="login-header">
                <h1>Welcome Back</h1>
                <p style={{textAlign: 'center'}}>Sign in to your Snapshot Videos account</p>
              </div>}

              <div className="login-form-container">
                {isLostPass ? (
                  <form onSubmit={handleLostPassSubmit} className="login-form">
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>

                    <div className="form-footer">
                      <span onClick={handleLostPass} className="link">
                        Back to Login
                      </span>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        className={errors.password ? 'error' : ''}
                      />
                      {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>

                    <div className="form-options">
                      <span onClick={handleLostPass} className="link">
                        Forgot Password?
                      </span>
                    </div>

                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={isLogin}
                    >
                      {isLogin ? 'Signing In...' : 'Sign In'}
                    </button>

                    <div className="divider">
                      <span>or</span>
                    </div>

                    <button 
                      type="button" 
                      className="btn-linkedin"
                      onClick={handleLinkedInLogin}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      Continue with LinkedIn
                    </button>

                    {/* <div className="form-footer">
                      <span>Don't have an account? <a href="/signup" className="link">Sign up</a></span>
                    </div> */}
                  </form>
                )}
              </div>
            </div>

            <div className="login-visual">
              <div className="visual-content">
                <h2>Quick, Engaging Updates for Your Members</h2>
                <p>Transform your association's communication with professional video content that keeps your members informed and engaged.</p>
                <div className="features">
                  <div className="feature-item">
                    <div className="feature-icon">🎥</div>
                    <span>Professional Video Production</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">⚡</div>
                    <span>Quick Turnaround</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">👥</div>
                    <span>Member Engagement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Login;
